import { CompanyWorker } from "model/Company";
import { CompanyGigShift } from "model/Gig";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import type { StaffProfileData } from "services/api/company/staff.service";
import { mapStaffProfileResponseV3ToData } from "services/api/company/staff.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import type { Worker as ModalWorker } from "../../../organizationalStructure/chooseWorkersModal.types";
import { ChooseWorkersModal } from "../../../organizationalStructure/chooseWorkersModal.component";
import { WorkerDropdown } from "../../../salary/workerDropDown.component";
import {
  ChooseWorkerSearchWrap,
  ChooseWorkersContainer,
  ScheduleMultipleButtonWrap,
  WorkerSelectionLockedInfo,
  WorkerListWrap,
} from "../createGigModal.styled";
import { WorkerList } from "./workerList.component";

type Props = {
  setSelectedWorkers: React.Dispatch<React.SetStateAction<ModalWorker[]>>;
  selectedWorkers: ModalWorker[];
  shifts: CompanyGigShift[];
};

export const ScheduleGigForm: React.FC<Props> = (props) => {
  const WORKER_DROPDOWN_WIDTH = 520;
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [selectedWorker, setSelectedWorker] = useState<
    CompanyWorker | undefined
  >(undefined);
  const [showChooseSeveral, setShowChooseSeveral] = useState(false);
  const workerSelectionLocked = props.shifts.length >= 1;

  const selectedWorkerKeys = useCallback(
    (worker: ModalWorker): string => String(worker.workerId ?? worker.id),
    [],
  );

  const selectedWorkerIdsKey = props.selectedWorkers
    .map(selectedWorkerKeys)
    .sort()
    .join("|");

  const toSelectedWorker = useCallback(
    (workerProfile: StaffProfileData, workerId: number): ModalWorker => {
      const externalId = workerProfile.externalId;
      return {
        firstName: workerProfile.firstName ?? "",
        lastName: workerProfile.lastName ?? "",
        personalIdentityNumber: workerProfile.personalIdentityNumber ?? "",
        workGroups: workerProfile.workGroups.map((workGroup) => ({
          id: workGroup.id,
          name: workGroup.name ?? "",
          workGroupId: workGroup.id,
          workGroupName: workGroup.name ?? "",
          companyUnitId: workGroup.companyUnitId,
          companyUnitName: workGroup.companyUnitName,
        })),
        employmentInfo: {
          current: {
            employmentType:
              workerProfile.employmentInfo?.current?.employmentType ?? "",
            paymentType:
              workerProfile.employmentInfo?.current?.paymentType ?? "",
          },
        },
        id: workerId,
        workerId,
        externalId: externalId ?? "",
      };
    },
    [],
  );

  useEffect(() => {
    const companyId = companyState.company?.id;
    const workerId = selectedWorker?.id;

    if (!companyId || !workerId) return;

    const alreadySelected = props.selectedWorkers.some(
      (worker) => selectedWorkerKeys(worker) === String(workerId),
    );
    if (alreadySelected) return;

    let cancelled = false;
    Api()
      .company.staff.getWorkerProfile(companyId, Number(workerId))
      .then((res) => {
        if (cancelled) return;
        const workerProfile = toSelectedWorker(
          mapStaffProfileResponseV3ToData(res.data),
          Number(workerId),
        );

        let didAdd = false;
        props.setSelectedWorkers((prev) => {
          if (
            prev.some(
              (worker) => selectedWorkerKeys(worker) === String(workerId),
            )
          ) {
            return prev;
          }
          didAdd = true;
          return [...prev, workerProfile];
        });

        if (didAdd) {
          setSelectedWorker(undefined);
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to load worker profile", {
          companyId,
          workerId,
          error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    toSelectedWorker,
    selectedWorkerKeys,
    selectedWorker?.id,
    companyState.company?.id,
    selectedWorkerIdsKey,
    props.setSelectedWorkers,
  ]);

  const addWorkersToList = useCallback(
    (selected: ModalWorker[]) => {
      setShowChooseSeveral(false);

      props.setSelectedWorkers((prev) => {
        const existing = new Set(prev.map(selectedWorkerKeys));

        const incoming = selected
          .map((worker) => ({
            ...worker,
            workerId: worker.workerId ?? worker.id,
          }))
          .filter(
            (worker) => !existing.has(String(worker.workerId ?? worker.id)),
          );

        return incoming.length ? [...prev, ...incoming] : prev;
      });
    },
    [props.setSelectedWorkers, selectedWorkerKeys],
  );

  return (
    <>
      <ChooseWorkersContainer>
        <ChooseWorkerSearchWrap>
          <WorkerDropdown
            selectedWorker={selectedWorker}
            setSelectedWorker={setSelectedWorker}
            onlyActiveWorkers
            width={WORKER_DROPDOWN_WIDTH}
            disabled={workerSelectionLocked}
            lazyLoad
          />
        </ChooseWorkerSearchWrap>
        <ScheduleMultipleButtonWrap>
          <SecondaryButton
            size="small"
            onClick={() => setShowChooseSeveral(true)}
            disabled={workerSelectionLocked}
            label={t("CreateShift.ChooseSeveral")}
          />
        </ScheduleMultipleButtonWrap>
      </ChooseWorkersContainer>
      {workerSelectionLocked && (
        <WorkerSelectionLockedInfo>
          <Text typography="b2" color={ColorV2.Blue4}>
            {t("CreateShift.WorkerSelectionLockedInfo")}
          </Text>
        </WorkerSelectionLockedInfo>
      )}

      <WorkerListWrap>
        <WorkerList
          selectedWorkers={props.selectedWorkers}
          setSelectedWorkers={props.setSelectedWorkers}
        />
      </WorkerListWrap>

      {showChooseSeveral && (
        <ChooseWorkersModal
          setShowModal={setShowChooseSeveral}
          showModal={showChooseSeveral}
          onClick={addWorkersToList}
          title={t("CreateShift.ChooseSeveral")}
          info={t("CreateShift.ChooseSeveralInfo")}
          buttonTitle={t("CreateShift.Choose")}
          selected={props.selectedWorkers}
          filter={"InWorkGroup"}
          showPaymentMethod
        />
      )}
    </>
  );
};
